import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import get from 'lodash/get';
import truncate from 'lodash/truncate';
import isEmpty from 'lodash/isEmpty';

import env from 'Env';
import StarRating from 'Components/ProductReviews/UserReviews/StarRating';
import MediaPreview from 'Components/Cards/MediaPreview';
import HitCardWishlistBtn from 'Components/Cards/HitCardWishlistBtn';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import store from 'Redux/store';
import HitLinkTo from 'Helpers/Product/HitLinkTo';
import hitToListItem from 'Components/Product/Seo/hit-list-item';
import 'Components/Cards/FanFavouriteCard.scss';
import cleanDisplayName from 'Components/Product/Seo/CleanDisplayName';
import { ScriptTag } from 'Helpers/SEO/Tags';


const FanFavouriteCard = ({
  hit,
  favouriteCardNameTag,
}) => {
  const { formatMessage } = useIntl();
  const messages = defineMessages({
    viewDetailFor: {
      id: 'userreviewcard.viewDetailFor',
      defaultMessage: 'View details for {name}',
    },
  });

  const {
    type,
    slug,
    cover,
    rating,
    likes,
    price,
    fullPrice,
    product_id: productId,
    display_type: displayType,
    game_total: gameTotal,
    dlc_total: dlcTotal,
    video_clip_poster: videoClipPoster,
    video_clip_files: videoClipFiles,
    screenshots,
    age_ratings: ageRatings,
    display_name: displayName,
    bundle_covers: bundleCovers,
    pay_what_you_want: payWhatYouWant,
    free_to_play: freeToPlay,
    presale,
    giveaway,
    tiered,
    discount_percent: discountPercent,
    hideDiscount,
    categories = [],
  } = hit;
  const [isHovering, setIsHovering] = useState(false);

  const formattedBundleCovers = get(hit, 'bundle_covers', []).map(b => b.cover);
  const text = get(hit, 'text', '');
  const isBundle = displayType === 'comic-bundle' || displayType === 'book-bundle' || displayType === 'elearning-bundle' || displayType === 'software-bundle' || displayType === 'audio-bundle' || type === 'bundle';

  let slides = screenshots;
  const isPromotion = (type === 'pick-and-mix');

  if ((isBundle || isPromotion) && formattedBundleCovers.length) {
    slides = formattedBundleCovers;
  }

  const onLinkClick = (link) => {
    store.dispatch({
      type: 'frontend/click/CLICK_INTERACTION',
      payload: { section: 'FanFavouriteCard', value: link },
    });
  };

  const productUrl = `${env.appProtocol}://${env.appUrl}/${window.locale || 'en'}/${type}/${slug}`;

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Review',
    '@id': `${productUrl}/reviews#${hit.review_id}`,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: hit.rating,
      bestRating: 5,
      worstRating: 1,
    },
    author: {
      '@type': 'Person',
      name: cleanDisplayName(displayName),
    },
    reviewBody: hit.text,
    inLanguage: hit.locale,
    datePublished: hit.review_date,
    itemReviewed: hitToListItem(hit, 0).item,
  });

  const reviewTextMd = `"${truncate(text.replace(/\n/g, ' '), { length: 125, separator: ' ' })}"`;
  let reviewTextLg = `"${truncate(text.replace(/\n/g, ' '), { length: 200, separator: ' ' })}"`;
  let reviewTextXl = `"${truncate(text.replace(/\n/g, ' '), { length: 250, separator: ' ' })}"`;
  let reviewTextXXl = `"${truncate(text.replace(/\n/g, ' '), { length: 300, separator: ' ' })}"`;

  if (!isEmpty(categories)) {
    reviewTextLg = `"${truncate(text.replace(/\n/g, ' '), { length: 150, separator: ' ' })}"`;
    reviewTextXl = `"${truncate(text.replace(/\n/g, ' '), { length: 200, separator: ' ' })}"`;
    reviewTextXXl = `"${truncate(text.replace(/\n/g, ' '), { length: 275, separator: ' ' })}"`;
  }

  const TagName = favouriteCardNameTag;

  return (
    <div className="fan-favourite-card">
      {env.enhanceItemLists &&
        <ScriptTag id={`site-schema-${slug}`} type="application/ld+json" script={schema} />
      }
      <div className="content-container">
        <div
          className="left-container faux-block-link"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >

          <div className="card-actions-container d-none d-lg-flex">
            {isHovering &&
              <HitCardWishlistBtn
                productId={productId}
                slug={slug}
                hit={hit}
                componentType="FanFavouriteCard"
              />
            }

            {!presale && !giveaway && !freeToPlay && !isHovering &&
              <PriceContainer
                discountPercent={discountPercent}
                price={price}
                tiered={tiered}
                hideDiscount={hideDiscount}
                slug={slug}
                fullPrice={fullPrice}
                payWhatYouWant={payWhatYouWant}
              />
            }
          </div>
          <Link
            to={HitLinkTo(hit)}
            className="w-100 faux-block-link__overlay-link"
            aria-label={formatMessage(messages.viewDetailFor, { name })}
            onClick={() => onLinkClick(`/${type}/${slug}`)}
          >
            <div className="cover-container">
              <MediaPreview
                disableVideo
                name={name}
                cover={cover}
                screenshots={screenshots}
                bundleCovers={bundleCovers}
                isHovering={isHovering}
                fullPrice={fullPrice}
                type={type}
                displayType={displayType}
                gameTotal={gameTotal + dlcTotal}
                videoClipPoster={videoClipPoster}
                videoClipFiles={videoClipFiles}
                slides={slides}
                ageRatings={ageRatings}
                sizesOverride="(min-width: 1520px) 590px, (min-width: 1200px) 500px, (min-width: 991px) 420px, (min-width: 768px) 310px, 98vw"
              />
            </div>
          </Link>
          <div className="card-actions-container d-lg-none">
            {!presale && !giveaway && !freeToPlay &&
              <PriceContainer
                discountPercent={discountPercent}
                price={price}
                tiered={tiered}
                hideDiscount={hideDiscount}
                slug={slug}
                fullPrice={fullPrice}
                payWhatYouWant={payWhatYouWant}
              />
            }
          </div>
        </div>
        <div className="right-container faux-block-link">
          <Link to={HitLinkTo(hit)} className="faux-block-link__overlay-link" onClick={() => onLinkClick(`/${type}/${slug}`)}>
            <TagName className="name-container">
              {hit.name}
            </TagName>
          </Link>
          <StarRating rating={rating} />

          <div className="price-rating-container">
            {!presale && !giveaway && !freeToPlay &&
              <PriceContainer
                discountPercent={discountPercent}
                price={price}
                tiered={tiered}
                hideDiscount={hideDiscount}
                slug={slug}
                fullPrice={fullPrice}
                payWhatYouWant={payWhatYouWant}
              />
            }
            <StarRating rating={rating} />
          </div>
          {!isEmpty(categories) &&
            <div className="tag-column-container">
              {categories.slice(0, 6).map(tag =>
                <div className="tag-column" key={tag}>
                  <div className="tag">
                    {tag}
                  </div>
                </div>,
              )}
            </div>
          }
          <div className="review-text-xs">
            {reviewTextMd}
          </div>
          <div className="review-text-sm">
            {reviewTextLg}
          </div>
          <div className="review-text-md">
            {reviewTextMd}
          </div>
          <div className="review-text-lg">
            {reviewTextLg}
          </div>
          <div className="review-text-xl">
            {reviewTextXl}
          </div>
          <div className="review-text-xxl">
            {reviewTextXXl}
          </div>
          <Link to={`/${type}/${slug}#product-reviews`} className="full-review-link" onClick={() => onLinkClick(`/${type}/${slug}#product-reviews`)}>
            <FormattedMessage id="fanfavourite.readfullreview" defaultMessage="Read full review" />
          </Link>
          <div className="bottom-container">
            <div className="display-name">
              {displayName && !!displayName.replace(/\s/g, '').length ?
                <React.Fragment>
                  {displayName}
                </React.Fragment>
                :
                <React.Fragment>
                  <FormattedMessage id="fanfavourite.fanaticaluser" defaultMessage="Fanatical User" />
                </React.Fragment>
              }
            </div>
            <div className="like-container">
              <div className="found-helpful-message">
                <FormattedMessage
                  id="userreviewcard.peoplefoundthishelpful"
                  defaultMessage="{likes, plural, =0 {No-one} one {# person} other {# people}} found this helpful"
                  values={{ likes }}
                />
              </div>
              <div className="like-button btn">
                <div className="like-button-icons-container">
                  <FontAwesomeIcon icon={faThumbsUp} />
                  <div className="like-count-container">
                    {likes}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FanFavouriteCard.propTypes = {
  hit: PropTypes.shape({}),
  favouriteCardNameTag: PropTypes.string,
};

FanFavouriteCard.defaultProps = {
  hit: {},
  favouriteCardNameTag: 'h2',
};

export default FanFavouriteCard;
